import * as React from 'react';
import {
  Box,
  Button,
  IconButton,
  Popover,
  Paper,
  TextField,
  Typography,
  Divider,
  Chip,
  useTheme,
} from '@mui/material';
import { CalendarMonth, Refresh, Close } from '@mui/icons-material';

interface DateRangeFilterProps {
  value: { from: string; to: string };
  onChange: (value: { from: string; to: string }) => void;
  onRefresh?: () => void;
  loading?: boolean;
  maxDate?: string;
}

function toInputDate(d: string | Date): string {
  let date: Date;
  if (typeof d === 'string') {
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(d)) {
      const [day, month, year] = d.split('/');
      date = new Date(Number(year), Number(month) - 1, Number(day));
    } else {
      date = new Date(d);
    }
  } else {
    date = d;
  }
  if (isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toOutputDate(d: string): string {
  if (!d) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  }
  const date = new Date(d);
  if (isNaN(date.getTime())) return d;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${day}/${m}/${y}`;
}

function toDisplayDate(d: string): string {
  if (!d) return '';
  let date: Date;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(d)) {
    const [day, month, year] = d.split('/');
    date = new Date(Number(year), Number(month) - 1, Number(day));
  } else {
    date = new Date(d);
  }
  if (isNaN(date.getTime())) return d;
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getQuickPresets(maxDate?: string): { label: string; from: string; to: string }[] {
  const today = maxDate ? new Date(maxDate) : new Date();
  const presets: { label: string; from: string; to: string }[] = [];

  const todayStr = toInputDate(today);

  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  presets.push({ label: '7 derniers jours', from: toInputDate(weekAgo), to: todayStr });

  const monthAgo = new Date(today);
  monthAgo.setMonth(monthAgo.getMonth() - 1);
  presets.push({ label: '30 derniers jours', from: toInputDate(monthAgo), to: todayStr });

  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  presets.push({ label: 'Ce mois-ci', from: toInputDate(monthStart), to: todayStr });

  const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  presets.push({ label: 'Mois dernier', from: toInputDate(prevMonthStart), to: toInputDate(prevMonthEnd) });

  const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
  presets.push({ label: 'Ce trimestre', from: toInputDate(quarterStart), to: todayStr });

  const yearStart = new Date(today.getFullYear(), 0, 1);
  presets.push({ label: 'Cette année', from: toInputDate(yearStart), to: todayStr });

  return presets;
}

export default function DateRangeFilter({
  value,
  onChange,
  onRefresh,
  loading,
  maxDate,
}: DateRangeFilterProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [tempFrom, setTempFrom] = React.useState(value.from);
  const [tempTo, setTempTo] = React.useState(value.to);

  const open = Boolean(anchorEl);
  const presets = React.useMemo(() => getQuickPresets(maxDate), [maxDate]);

  React.useEffect(() => {
    setTempFrom(value.from);
    setTempTo(value.to);
  }, [value]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onChange({ from: toOutputDate(tempFrom), to: toOutputDate(tempTo) });
    handleClose();
  };

  const handlePreset = (preset: { from: string; to: string }) => {
    setTempFrom(preset.from);
    setTempTo(preset.to);
  };

  const handleReset = () => {
    const today = maxDate ? toInputDate(maxDate) : toInputDate(new Date());
    setTempFrom(today);
    setTempTo(today);
  };

  const hasCustomRange = value.from !== value.to;

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Button
        onClick={handleClick}
        variant="outlined"
        size="small"
        startIcon={<CalendarMonth />}
        disabled={loading}
        sx={{
          borderRadius: '10px',
          textTransform: 'none',
          borderColor: '#cbd5e1',
          color: '#334155',
          bgcolor: '#fff',
          px: 2,
          py: 0.75,
          fontWeight: 600,
          fontSize: 13,
          '&:hover': { borderColor: theme.palette.primary.main, bgcolor: '#f8fafc' },
        }}
      >
        {hasCustomRange ? (
          <Box component="span" display="flex" alignItems="center" gap={0.5}>
            {toDisplayDate(value.from)}
            <Box component="span" sx={{ color: '#94a3b8', mx: 0.5 }}>→</Box>
            {toDisplayDate(value.to)}
          </Box>
        ) : (
          toDisplayDate(value.from)
        )}
      </Button>

      {onRefresh && (
        <IconButton
          onClick={onRefresh}
          disabled={loading}
          size="small"
          sx={{
            border: '1px dashed #cbd5e1',
            borderRadius: '10px',
            height: '36px',
            width: '36px',
            color: '#64748b',
            '&:hover': { borderColor: theme.palette.primary.main, color: theme.palette.primary.main, bgcolor: '#f8fafc' },
          }}
        >
          <Refresh sx={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
        </IconButton>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px -5px rgba(0,0,0,0.15)',
            mt: 1,
          },
        }}
      >
        <Paper elevation={0} sx={{ width: 420, p: 0 }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              bgcolor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarMonth sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight="bold" color="#1e293b">
                Sélectionner la période
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleClose} sx={{ color: '#94a3b8' }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* Quick presets */}
          <Box sx={{ p: 2 }}>
            <Typography variant="caption" fontWeight="bold" color="#64748b" sx={{ mb: 1, display: 'block' }}>
              RACCOURCIS
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {presets.map((preset) => {
                const isActive = tempFrom === preset.from && tempTo === preset.to;
                return (
                  <Chip
                    key={preset.label}
                    label={preset.label}
                    size="small"
                    onClick={() => handlePreset(preset)}
                    sx={{
                      borderRadius: '8px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      bgcolor: isActive ? theme.palette.primary.main : '#f1f5f9',
                      color: isActive ? '#fff' : '#475569',
                      border: isActive ? 'none' : '1px solid #e2e8f0',
                      '&:hover': {
                        bgcolor: isActive ? theme.palette.primary.dark : '#e2e8f0',
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          <Divider />

          {/* Custom date inputs */}
          <Box sx={{ p: 2 }}>
            <Typography variant="caption" fontWeight="bold" color="#64748b" sx={{ mb: 1, display: 'block' }}>
              PÉRIODE PERSONNALISÉE
            </Typography>
            <Box display="flex" alignItems="center" gap={1.5}>
              <TextField
                type="date"
                label="Du"
                size="small"
                value={tempFrom}
                onChange={(e) => setTempFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: maxDate || tempTo || undefined }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    fontSize: 13,
                  },
                }}
              />
              <Box sx={{ color: '#94a3b8', fontWeight: 'bold' }}>→</Box>
              <TextField
                type="date"
                label="Au"
                size="small"
                value={tempTo}
                onChange={(e) => setTempTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempFrom || undefined,
                  max: maxDate || undefined,
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    fontSize: 13,
                  },
                }}
              />
            </Box>
          </Box>

          {/* Footer actions */}
          <Box
            sx={{
              p: 1.5,
              bgcolor: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
              borderRadius: '0 0 12px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button
              size="small"
              onClick={handleReset}
              sx={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}
            >
              Réinitialiser
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApply}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                fontWeight: 700,
                px: 3,
                boxShadow: 'none',
                '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
              }}
            >
              Appliquer
            </Button>
          </Box>
        </Paper>
      </Popover>
    </Box>
  );
}
